/**
 * SchemaContextMenu — Right-click context menu for schema tree objects.
 *
 * Reuses pattern from src/components/editor/ContextMenu.jsx:
 * - Fixed position at click coordinates
 * - Viewport edge adjustment
 * - Escape / outside-click to close
 */

import { useEffect, useRef } from 'react'
import {
  RefreshCw, Play, Download, Copy, Edit3,
  CheckSquare, Square, Eye
} from 'lucide-react'

// Source tree menu items
const SOURCE_ITEMS = [
  { id: 'convert', label: 'Convert This Object', icon: RefreshCw },
  { id: 'view-ddl', label: 'View DDL', icon: Eye },
  { id: 'copy-ddl', label: 'Copy DDL to Clipboard', icon: Copy },
  { id: 'separator-1', separator: true },
  { id: 'select-all-category', label: 'Select All in Category', icon: CheckSquare },
  { id: 'deselect-all-category', label: 'Deselect All in Category', icon: Square },
]

// Target tree menu items
const TARGET_ITEMS = [
  { id: 'deploy', label: 'Deploy This Object', icon: Play },
  { id: 'export-sql', label: 'Export as SQL', icon: Download },
  { id: 'edit-ddl', label: 'Edit DDL', icon: Edit3 },
  { id: 'copy-ddl', label: 'Copy DDL to Clipboard', icon: Copy },
  { id: 'separator-1', separator: true },
  { id: 'select-all-category', label: 'Select All in Category', icon: CheckSquare },
  { id: 'deselect-all-category', label: 'Deselect All in Category', icon: Square },
]

function SchemaContextMenu({ x, y, tree, objectType, objectName, onSelect, onClose }) {
  const menuRef = useRef(null)

  // Close on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose()
    }
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose])

  // Adjust position to stay in viewport
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect()
      if (rect.right > window.innerWidth) {
        menuRef.current.style.left = `${x - rect.width}px`
      }
      if (rect.bottom > window.innerHeight) {
        menuRef.current.style.top = `${y - rect.height}px`
      }
    }
  }, [x, y])

  const items = tree === 'source' ? SOURCE_ITEMS : TARGET_ITEMS

  return (
    <div
      ref={menuRef}
      className="schema-context-menu"
      style={{ left: x, top: y }}
    >
      <div className="schema-context-header">
        {objectName}
      </div>
      <div className="schema-context-subheader">
        {objectType}
      </div>
      <div className="schema-context-separator" />
      {items.map(item => {
        if (item.separator) {
          return <div key={item.id} className="schema-context-separator" />
        }
        const Icon = item.icon
        return (
          <button
            key={item.id}
            className="schema-context-item"
            onClick={() => { onSelect(item.id); onClose() }}
          >
            <Icon size={14} />
            <span>{item.label}</span>
          </button>
        )
      })}
    </div>
  )
}

export default SchemaContextMenu
